// import { NewAlert } from "@/types";
import DialogUpdateProfile from "@/components/DialogUpdateProfile/dialogUpdateProfile.component";
import { ROUTE_LOGIN_OUT, ROUTE_PROFILE } from "@/constants/routes.constants";
import { AppContext, defaultState } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { NewAlert, RoleName } from "@/types";
import {
  getRoleFromToken,
  getSessionDismissedUpdateAccount,
  getSessionShouldRegisterAccount,
  getSessionShouldUpdateAccount,
  getUserDetailsFromToken,
} from "@/utils/tokens.util";
import { getAlertTypeString, getMilliseconds } from "@/utils/utils.util";
import LogRocket from "logrocket";
import React, { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import Swal from "sweetalert2";

const MAX_ALERTS = 50;

interface AppProviderProps {
  children: ReactNode;
}
const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [allAlerts, setAllAlerts] = useState<NewAlert[]>(
    defaultState.alertData
  );
  const [newAlerts, setNewAlerts] = useState<NewAlert[]>(
    defaultState.newAlerts
  );
  const nav = useNavigate();
  const [isNavbarAlertsOpen, setIsNavbarAlertsOpen] = useState<boolean>(
    defaultState.isNavbarAlertsOpen
  );
  const [userRole, setUserRole] = useState<RoleName | undefined>(undefined);
  const [userDismissedUpdateAccount, setUserDismissedUpdateAccount] =
    useState<boolean>(defaultState.userDismissedUpdateAccount);
  const [userShouldUpdateAccount, setUserShouldUpdateAccount] =
    useState<boolean>(defaultState.userShouldUpdateAccount);
  const [userShouldRegisterAccount, setUserShouldRegisterAccount] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    if (newAlerts.length === 0) {
      toast.dismiss("device-alert");
      return;
    }

    if (isNavbarAlertsOpen) {
      toast.dismiss("device-alert");
      setNewAlerts([]);
      return;
    }

    newAlerts.forEach((alert) => {
      toast("", {
        id: "device-alert",
        duration: 5000,
        style: { padding: "12px" },
        action: (
          <div className="flex w-full flex-row flex-nowrap items-center justify-between gap-2 text-xs md:text-[13px]">
            <div>
              <div className="font-bold">
                {getAlertTypeString(alert.alertType)}
              </div>
            </div>
            <Button
              className="text-[13px] md:text-sm"
              onClick={() => {
                toast.dismiss("device-alert");
                setNewAlerts([]);
              }}
            >
              Dismiss
            </Button>
          </div>
        ),
      });
    });
  }, [newAlerts, isNavbarAlertsOpen, nav]);

  useLayoutEffect(() => {
    const userTokenRole = getRoleFromToken();
    setUserRole(userTokenRole);

    const environment = import.meta.env.VITE_ENVIRONMENT;
    if (!environment || environment !== "prod") {
      console.warn(
        "AppProvider: Not in production environment, skipping alert initialization."
      );
      return;
    }

    const tokeUserDetails = getUserDetailsFromToken();
    if (!tokeUserDetails) {
      console.error("AppProvider: No user details found in token.");
      return;
    }

    LogRocket.identify(tokeUserDetails.email, {
      email: tokeUserDetails.email,
      role: tokeUserDetails.role,
    });
  }, []);

  useEffect(() => {
    console.log("userRole", userRole);
    if (!userRole) {
      return;
    }

    if (userRole !== "approved_user") {
      setUserShouldRegisterAccount(false);
      return;
    }

    const shouldShowRegisterAccount = getSessionShouldRegisterAccount();

    setUserShouldRegisterAccount(shouldShowRegisterAccount);
    if (shouldShowRegisterAccount) {
      console.log("shouldShowRegisterAccount", shouldShowRegisterAccount);
      setUserShouldUpdateAccount(true);
      setUserDismissedUpdateAccount(false);
      nav(ROUTE_PROFILE);
      return;
    }

    const shouldShowUpdateAccount = getSessionShouldUpdateAccount();
    setUserShouldUpdateAccount(shouldShowUpdateAccount);
    if (!shouldShowUpdateAccount) {
      return;
    }
    console.log("shouldShowUpdateAccount", shouldShowUpdateAccount);
    const sessionDismissedUpdateAccount = getSessionDismissedUpdateAccount();
    console.log("sessionDismissedUpdateAccount", sessionDismissedUpdateAccount);
    if (sessionDismissedUpdateAccount) {
      return;
    }
    setUserDismissedUpdateAccount(sessionDismissedUpdateAccount);
    nav(ROUTE_PROFILE);
  }, [userRole, nav]);

  const onIdle = () => {
    console.warn("User is idle");
    Swal.fire({
      theme: "dark",
      title: "Session Expired",
      html: "Your session has expired due to inactivity.<br />Please log in again.",
      icon: "warning",
      // custom button style
      confirmButtonText: "Ok",
      confirmButtonColor: "#323864",
    });

    nav(ROUTE_LOGIN_OUT);
  };

  useIdleTimer({
    onIdle,
    timeout: getMilliseconds({ d: 0, h: 1, m: 0, s: 0 }),
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
  });

  if (!userRole) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  if (userShouldRegisterAccount === undefined) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <AppContext.Provider
      value={{
        alertData: allAlerts,
        newAlerts: newAlerts,
        isNavbarAlertsOpen: isNavbarAlertsOpen,
        setIsNavbarAlertsOpen: setIsNavbarAlertsOpen, // provide the state setter
        userRole: userRole, // provide the user role
        userDismissedUpdateAccount: userDismissedUpdateAccount, // provide the user dismissed update account state
        setUserDismissedUpdateAccount: setUserDismissedUpdateAccount, // provide the state setter for user dismissed update account
        userShouldUpdateAccount: userShouldUpdateAccount, // provide the user should update account state
        userShouldRegisterAccount: userShouldRegisterAccount, // provide the user should register account state
        setUserShouldUpdateAccount: setUserShouldUpdateAccount, // provide the state setter for user should update account
      }}
    >
      {children}
      {!userDismissedUpdateAccount && <DialogUpdateProfile />}
    </AppContext.Provider>
  );
};

export default AppProvider;
